/**
 * The sidebar's right-aligned counts (TMMAPP-0100; TMMAPP-0150 U2: small pills on their Money Tools
 * rows): 12px tabular text on a `surface2` pill, not an MUI `Badge`. My Accounts shows the review
 * total (TMMAPP-0159 §F 33, replacing the account count; TMMAPP-0031: `GET /me/review/summary` —
 * `total` already includes `reimbursementProposals`, never add it again). Recurring has no badge: its
 * Items to review are the suggested bills, a count the shell doesn't hold. A count of zero, an
 * unloaded count and a failed read all render nothing.
 *
 * The pill is the opaque canvas the sidebar doesn't paint (`surface` on a `surface2` sidebar, else
 * `surface2`, TMMAPP-0151 §A 5) with `text2`, a guaranteed pair whatever the row under it paints
 * (the sidebar's canvas, the `hover` layer or the current row's `brandSoft`).
 */
import { useEffect, useState } from 'react';
import { Typography, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { reviewApi } from '@/api/review';
import { useMe } from '@/session/meHooks';
import { PILL_RADIUS, resolveTokens } from '@/theme';
import { REVIEW_CHANGED_EVENT } from './reviewChanged';
import { sidebarCanvas } from './sidebarPaper';

function NavCount({ count, label }: { count: number; label: string }) {
  const tokens = resolveTokens(useTheme());
  return (
    <Typography
      variant="caption"
      component="span"
      role="img"
      aria-label={label}
      data-nav-count=""
      sx={{
        flexShrink: 0,
        px: '7px',
        borderRadius: `${PILL_RADIUS}px`,
        bgcolor: sidebarCanvas(tokens) === 'surface2' ? tokens.surface : tokens.surface2,
        color: tokens.text2,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {count}
    </Typography>
  );
}

/**
 * My Accounts' badge (TMMAPP-0159 §F 33): the review total, "{count} to review" — every account's
 * Items to review, which live on each account's page. Reloads as the person leaves or moves within
 * My Accounts, where those items are resolved, and when a page announces a change
 * (`REVIEW_CHANGED_EVENT`, TMMAPP-0163: a payee-naming run's new suggestions).
 */
export function ReviewNavCount() {
  const { t } = useTranslation();
  const me = useMe();
  const { pathname } = useLocation();
  const [total, setTotal] = useState(0);
  const [changed, setChanged] = useState(0);
  const scope = pathname.startsWith('/me/accounts') ? pathname : 'elsewhere';

  useEffect(() => {
    const onChange = () => setChanged((n) => n + 1);
    window.addEventListener(REVIEW_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(REVIEW_CHANGED_EVENT, onChange);
  }, []);

  useEffect(() => {
    if (me.tier !== 'SUBSCRIBER') return;
    let cancelled = false;
    void reviewApi
      .summary()
      .then((summary) => {
        if (!cancelled) setTotal(summary.total);
      })
      .catch(() => {
        if (!cancelled) setTotal(0);
      });
    return () => {
      cancelled = true;
    };
  }, [me.tier, scope, changed]);

  if (total <= 0) return null;
  return <NavCount count={total} label={t('shell.reviewCount', { count: total })} />;
}
